import React, { useState, useEffect } from "react";
import { CardTransaction } from "../../../types/api/card";
import { TRANSACTION_TYPES } from "../../../constants/enums";
import { formatDateLongWithWeekday, generateUniqueInteger } from "../../../services/formatter";

import ChevronRight from "../../../assets/images/icons/chevron-right-gray.svg";
import CashPickupImg from "../../../assets/images/icons/cash-pickup.svg";
import BankImg from "../../../assets/images/icons/bank.svg";
import MobileWalletImg from "../../../assets/images/icons/mobile-wallet.svg";
import CreditCardUploadImg from "../../../assets/images/icons/credit-card-upload.svg";

import ActionLink from "../../Common/ActionLink";
import { TransactionsListProps } from "../../../types/components";

const TransactionsList = ({ card, setTransactionToGet, setFromClick, getMoreTransactions, disabled }: TransactionsListProps) => {
  const [transactionsElements, setTransactionsElements] = useState<React.ReactNode[]>([]);
  const [active, setActive] = useState<string | null>(null);

  /** 
   * On initial render, set active transaction and fetch details
   */
  useEffect(() => {
    if (card?.transactions) {
      setActive(createUniqueIdentifier(card.transactions[0]));
      setTransactionToGet(card.transactions[0]);
      setFromClick(false);
    }
  }, []);

  /**
   * Whenever transactions change (i.e. user clicks view more transactions) 
   */
  useEffect(() => {
    setTransactionsElements([]);
    const dates = mapTransactionsToDate(card?.transactions ?? []);
    handleSetTransactionsElements(dates);
  }, [card?.transactions, active]);

  const handleClick = (transaction: CardTransaction) => {
    setActive(createUniqueIdentifier(transaction));
    setTransactionToGet(transaction);
    setFromClick(true);
  }

  const createUniqueIdentifier = (transaction: CardTransaction) => {
    const { date, type, amount, description } = transaction;
    return `${date}-${type}-${amount}-${description}`;
  }

  const handleSetTransactionsElements = (dates: Record<string, CardTransaction[]>) => {
    for (const key in dates) {
      const dateGroup = dates[key];
      const dateElement = (
        <div key={generateUniqueInteger()} className="transaction-group">
          <h4>{formatDateLongWithWeekday(new Date(key))}</h4>
          <ul className="transaction-list">
            {dateGroup.map((transaction: CardTransaction) => {
              const imageSrc = transaction?.logo?.url ?? getTransactionTypeImage(transaction.type);
              // if there is an active element (i.e. user has clicked an <li> item), then set active class on that element
              // else if there is no active element (i.e. recent transactions has just loaded and user hasn't clicked anything) then set active class on the first element
              const isActive = active === createUniqueIdentifier(transaction);
              return (
                <li key={generateUniqueInteger()} className={`${isActive ? "active" : ""} transaction-list-item`}>
                  <button onClick={() => handleClick(transaction)} className="no-styling" data-testid="transaction-list-button">
                    <div className="flex-row align-items-center">
                      <img src={imageSrc} alt="" />
                      {transaction?.merchantData?.name
                        ? <span>{transaction?.merchantData?.name}</span>
                        : <span dangerouslySetInnerHTML={{ __html: transaction?.htmlDescription }} className="text-left"></span>
                      }
                    </div>

                    <div className="amount flex-row align-items-center">
                      <div>
                        <p className={`spacing-bottom-none ${transaction.isCredit ? "alert-green" : "marengo-grey"} text-right`}>{transaction.formattedAmount}</p>
                        <p className="spacing-bottom-none marengo-grey text-right font-size-sm">{transaction.status}</p>
                      </div>
                      <img src={ChevronRight} alt="chevron right" />
                    </div>

                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      );
      setTransactionsElements(prev => [...prev, dateElement]);
    }
  }

  const mapTransactionsToDate = (transactions: CardTransaction[]) => {
    return transactions.reduce<Record<string, CardTransaction[]>>((dates, transaction) => {
      const date = new Date(transaction.date);
      const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      (dates[dateKey] ??= []).push(transaction);
      return dates;
    }, {});
  };

  const getTransactionTypeImage = (transactionType: number) => {
    switch (transactionType) {
      case TRANSACTION_TYPES.ODT:
      case TRANSACTION_TYPES.MC_TO_ACCOUNT:
        return BankImg;
      case TRANSACTION_TYPES.MOBILE_WALLET:
        return MobileWalletImg;
      case TRANSACTION_TYPES.PUSH_TO_CARD:
        return CreditCardUploadImg;
      case TRANSACTION_TYPES.MONEY_TRANSFER:
      default:
        return CashPickupImg;
    }
  }

  return (
    <section className="transactions-container">
      <div className="transactions-list">
        {transactionsElements}
      </div>


      <article className="transactions-button">
        <ActionLink
          text="View more transactions"
          clickFunc={getMoreTransactions}
          disabled={disabled}
        />
      </article>
    </section>
  );
}

export default TransactionsList;